import React from 'react';
import { Button, InputLabel, Modal, toast, Loader, SelectRadios } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import Inventory from '../../models/inventory';
import Partner from '../../models/partner';
import { useNotifications, Notification } from '../../components/notifications/notificationsProvider';
import InventoriesPicker from '../../components/charts/inventoriesPicker';

export default function InventorySelectionModal({
    showModal = false,
    setShowModal = () => {},
    organization,
    selectedPartners = {},
    setSelectedPartners = () => {},
    setAllPartnersSelected = () => {}
}) {
    if (!showModal) return null;

    const { t } = useTranslation();

    const { addNotification } = useNotifications();
    const [loading, setLoading] = React.useState(false);
    const [selectAllInventories, setSelectAllInventories] = React.useState(true);
    const [selectedInventories, setSelectedInventories] = React.useState([]);
    const [inventories, loadingInventories] = Inventory.useApiModel(
        {
            organizationId: organization.id,
            allPartners: true,
            defaultLoading: false,
            launch: showModal && !selectAllInventories
        },
        [organization, showModal, selectAllInventories]
    );

    const onClose = React.useCallback(() => {
        setShowModal(false);
        setAllPartnersSelected(false);
        setSelectedPartners({});
    }, []);

    const handleSubmit = React.useCallback(() => {
        if (selectedPartners.length <= 0) return;

        setLoading(true);
        const selectedPartnerIds = Object.keys(selectedPartners);
        const args = { organization, partnerIds: selectedPartnerIds };

        if (!selectAllInventories) {
            args.inventoryIds = selectedInventories.map(i => i.pk);
        }

        Partner.async_perform_action_free_bookings(args)
            .then(response => {
                addNotification(
                    new Notification('async_actions', 'async_perform_action_free_tickets', 'partners', response?.response?.task_id, {
                        success: 'ticket.partnersList.freeBookings.toast-success',
                        error: 'ticket.partnersList.freeBookings.toast-error'
                    })
                );
                toast.info(t('ticket.partnersList.freeBookings.toast-in-progress'));
            })
            .catch(error => {
                console.error(error);
                toast.error(t('ticket.partnersList.freeBookings.toast-error'));
            })
            .finally(() => {
                setLoading(false);
                onClose();
            });
    }, [organization, selectedPartners, selectedInventories, selectAllInventories]);

    return (
        <Modal
            size="large"
            allowExternalClosing={true}
            open={showModal}
            onClose={onClose}
            title={t('ticket.partnersList.freeBookings.modalTitle')}
            subtitle={t('ticket.partnersList.freeBookings.modalSubtitle')}
            cancelButton={<Button onClick={onClose} label={t('ticket.common.action.cancel')} />}
            submitButton={
                <Button
                    primary
                    disabled={selectedInventories?.length <= 0 && !selectAllInventories}
                    isLoading={loading}
                    onClick={handleSubmit}
                    label={t('ticket.common.action.confirm')}
                />
            }
        >
            <SelectRadios
                options={[
                    {
                        text: t('ticket.partnersList.freeBookings.selectAllInventories'),
                        value: true
                    },
                    {
                        text: t('ticket.partnersList.freeBookings.inventorySelection'),
                        value: false
                    }
                ]}
                value={selectAllInventories}
                onChange={setSelectAllInventories}
            />
            {!selectAllInventories &&
                (loadingInventories ? (
                    <Loader inline="centered" style={{ overflow: 'hidden' }}></Loader>
                ) : (
                    <>
                        <InputLabel required label={t('ticket.partnersList.freeBookings.inventorySelectionTitle')}></InputLabel>
                        {inventories && (
                            <InventoriesPicker
                                onSave={setSelectedInventories}
                                organizationId={organization.id}
                                activeInventories={inventories.filter(inventory => inventory.event)}
                                currentSelected={selectedInventories}
                            />
                        )}
                    </>
                ))}
        </Modal>
    );
}
